import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/energieglueck-logo-optimized.png";
import logoWhite from "@/assets/energieglueck-logo-white.png";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import "@/components/navigation.css";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();
  const { pathname } = useLocation();
  // Transparente Top-Nav nur dort, wo ein randloses Hero-Bild direkt unter der Leiste
  // liegt: die Journey auf "/" (seit 04.09.2026) und der Redirect /neu.
  const isHome = pathname === "/" || pathname === "/neu";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY >= 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navState = scrolled || !isHome ? "eg-nav--solid" : "eg-nav--top";

  return (
    <nav className={`eg-nav fixed top-0 left-0 right-0 z-50 ${navState}`}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo links */}
          <Link to="/" className="eg-nav__brand flex-shrink-0 flex items-center">
            {/* Dunkles Logo im transparenten Hero, weißes Logo sobald der Balken kommt (CSS blendet um) */}
            <img src={logo} alt="Energieglück - wir speichern Zukunft" className="eg-nav__logo eg-nav__logo--dark h-12 md:h-16 w-auto object-contain" loading="eager" />
            <img src={logoWhite} alt="" aria-hidden="true" className="eg-nav__logo eg-nav__logo--light h-12 md:h-16 w-auto object-contain" loading="eager" />
          </Link>

          {/* Nav mittig */}
          <div className="hidden lg:flex items-center justify-center flex-1 space-x-2">
            <Link to="/" className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors">
              Home
            </Link>

            {/* 1. KI Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("ki")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                {t('ai.nav.title')} <ChevronDown className="w-4 h-4" />
              </button>
              {activeDropdown === "ki" && (
                <div className="absolute top-full left-0 -mt-1 pt-2 w-64 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-border py-2">
                  <Link
                    to="/ki"
                    className="block px-4 py-2 text-sm font-semibold text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
                  >
                    {t('ai.nav.overview')}
                  </Link>
                  <Link
                    to="/ki/reachy"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('ai.nav.reachy')}
                  </Link>
                  <Link
                    to="/ki/projektmanagement"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('ai.nav.projektmanagement')}
                  </Link>
                  <Link
                    to="/ki/use-cases"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('ai.nav.useCases')}
                  </Link>
                  <div className="border-t border-border my-1" />
                  <Link
                    to="/ki/playground"
                    className="block px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors"
                  >
                    {t('ai.nav.playground')}
                  </Link>
                  </div>
                </div>
              )}
            </div>

            {/* 2. Batteriespeicher Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("bess")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                {t('nav.batteriespeicher')} <ChevronDown className="w-4 h-4" />
              </button>
              {activeDropdown === "bess" && (
                <div className="absolute top-full left-0 -mt-1 pt-2 w-64 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-border py-2">
                  <Link
                    to="/gridstable"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    GridStable
                  </Link>
                  <Link
                    to="/flaecheneigentuemer"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.flaecheneigentuemer')}
                  </Link>
                  <Link
                    to="/netzbetreiber"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.netzbetreiber')}
                  </Link>
                  <Link
                    to="/kommunen"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.kommunen')}
                  </Link>
                  </div>
                </div>
              )}
            </div>

            {/* 3. Energieglück+ Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("egplus")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                {t('nav.energieglueck')} <ChevronDown className="w-4 h-4" />
              </button>
              {activeDropdown === "egplus" && (
                <div className="absolute top-full left-0 -mt-1 pt-2 w-64 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-border py-2">
                  <Link
                    to="/plattform"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('plattform.nav.dd.uebersicht')}
                  </Link>
                  <Link
                    to="/plattform/anwendungen"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('plattform.nav.dd.anwendungen')}
                  </Link>
                  <Link
                    to="/plattform/wirtschaftlichkeit"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('plattform.nav.dd.wirtschaftlichkeit')}
                  </Link>
                  <Link
                    to="/plattform/referenzen"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('plattform.nav.dd.referenzen')}
                  </Link>
                  </div>
                </div>
              )}
            </div>

            {/* 4. Über uns Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown("about")}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1">
                {t('nav.aboutUs')} <ChevronDown className="w-4 h-4" />
              </button>
              {activeDropdown === "about" && (
                <div className="absolute top-full left-0 -mt-1 pt-2 w-56 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-border py-2">
                  <Link
                    to="/unternehmen/ueber-uns"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.about')}
                  </Link>
                  <Link
                    to="/unternehmen/projekte"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.projects')}
                  </Link>
                  <Link
                    to="/presse"
                    className="block px-4 py-2 text-sm text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
                  >
                    {t('nav.press')}
                  </Link>
                  </div>
                </div>
              )}
            </div>

            {/* 5. Kontakt als Textlink */}
            <Link to="/kontakt" className="eg-nav__link px-4 py-2 text-sm font-medium transition-colors">
              {t('nav.contact')}
            </Link>
          </div>

          {/* CTA rechts — nur 1 Button */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <div className="eg-nav__lang">
              <LanguageSwitcher />
            </div>
            <Link to="/ki/playground" className="eg-nav__cta">
              {t('nav.playground')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="eg-nav__link lg:hidden p-2 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="eg-nav__mobile lg:hidden py-4 border-t border-border bg-white">
            <div className="flex flex-col space-y-2">
              <div className="eg-nav__lang px-4 py-2">
                <LanguageSwitcher />
              </div>

              {/* KI */}
              <div className="px-4 py-2">
                <div className="font-medium text-sm text-foreground mb-2">{t('ai.nav.title')}</div>
                <div className="pl-4 space-y-2">
                  <Link to="/ki" className="block text-sm font-semibold text-primary hover:text-primary-dark" onClick={() => setIsOpen(false)}>
                    {t('ai.nav.overview')}
                  </Link>
                  <Link to="/ki/reachy" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('ai.nav.reachy')}
                  </Link>
                  <Link to="/ki/projektmanagement" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('ai.nav.projektmanagement')}
                  </Link>
                  <Link to="/ki/use-cases" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('ai.nav.useCases')}
                  </Link>
                  <Link to="/ki/playground" className="block text-sm font-semibold text-primary hover:text-primary-dark" onClick={() => setIsOpen(false)}>
                    {t('ai.nav.playground')}
                  </Link>
                </div>
              </div>

              {/* Batteriespeicher */}
              <div className="px-4 py-2">
                <div className="font-medium text-sm text-foreground mb-2">{t('nav.batteriespeicher')}</div>
                <div className="pl-4 space-y-2">
                  <Link to="/gridstable" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    GridStable
                  </Link>
                  <Link to="/flaecheneigentuemer" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.flaecheneigentuemer')}
                  </Link>
                  <Link to="/netzbetreiber" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.netzbetreiber')}
                  </Link>
                  <Link to="/kommunen" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.kommunen')}
                  </Link>
                </div>
              </div>

              {/* Energieglück+ */}
              <div className="px-4 py-2">
                <div className="font-medium text-sm text-foreground mb-2">{t('nav.energieglueck')}</div>
                <div className="pl-4 space-y-2">
                  <Link to="/plattform" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('plattform.nav.dd.uebersicht')}
                  </Link>
                  <Link to="/plattform/anwendungen" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('plattform.nav.dd.anwendungen')}
                  </Link>
                  <Link to="/plattform/wirtschaftlichkeit" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('plattform.nav.dd.wirtschaftlichkeit')}
                  </Link>
                  <Link to="/plattform/referenzen" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('plattform.nav.dd.referenzen')}
                  </Link>
                </div>
              </div>

              {/* Über uns */}
              <div className="px-4 py-2">
                <div className="font-medium text-sm text-foreground mb-2">{t('nav.aboutUs')}</div>
                <div className="pl-4 space-y-2">
                  <Link to="/unternehmen/ueber-uns" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.about')}
                  </Link>
                  <Link to="/unternehmen/projekte" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.projects')}
                  </Link>
                  <Link to="/presse" className="block text-sm text-muted-foreground hover:text-primary" onClick={() => setIsOpen(false)}>
                    {t('nav.press')}
                  </Link>
                </div>
              </div>

              <Link to="/ki/playground" className="eg-nav__cta eg-nav__cta--block mx-4 mt-4" onClick={() => setIsOpen(false)}>
                {t('nav.playground')}
              </Link>
              <Link to="/kontakt" className="mx-4 mt-2 block text-center text-sm text-primary font-medium hover:underline" onClick={() => setIsOpen(false)}>
                {t('nav.contact')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
